import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertTriangle, MapPin, Camera, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIAS = [
  { value: 'Limpieza Viaria', label: 'Limpieza Viaria', icon: '🧹' },
  { value: 'Recogida RSU', label: 'Recogida de Residuos', icon: '♻️' },
  { value: 'Playas y Litoral', label: 'Playas y Litoral', icon: '🏖️' },
  { value: 'Parques y Jardines', label: 'Parques y Jardines', icon: '🌳' },
  { value: 'Otros servicios', label: 'Otros Servicios', icon: '🔧' }
];

const TIPOS_LIMPIEZA = [
  'Manchas en pavimento',
  'Retirada de papeleras',
  'Sustitución de papeleras',
  'Papeleras llenas',
  'Bolsas fuera de contenedores',
  'Residuos en vía pública',
  'Excrementos de animales',
  'Grafitis',
  'Otros'
];

const TIPOS_RECOGIDA = [
  'Contenedor en mal estado',
  'Contenedor quemado',
  'Contenedor pintado',
  'Contenedor lleno',
  'Contenedor no recogido',
  'Otros'
];

const TIPOS_PLAYAS = [
  'Suciedad en la playa',
  'Residuos / Flotantes en el agua',
  'Papeleras playa en mal estado o ausentes',
  'Duchas o lavapiés averiados',
  'Pasarelas en mal estado',
  'Otros'
];

const TIPOS_JARDINES = [
  'Riego deficiente o averiado',
  'Poda necesaria de árbol o arbusto',
  'Zonas verdes sucias / abandonadas',
  'Jardineras en mal estado',
  'Otros'
];

const PRIORIDADES = [
  { value: 'Baja', label: 'Baja', color: 'bg-green-100 text-green-800' },
  { value: 'Media', label: 'Media', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'Alta', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  { value: 'Urgente', label: 'Urgente', color: 'bg-red-100 text-red-800' }
];

export default function ReportarIncidencia() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [incidenciaId, setIncidenciaId] = useState(null);
  
  const [formData, setFormData] = useState({
    procedencia: 'Ciudadanos',
    categoria: '',
    tipo_incidencia: '',
    Direccion: '',
    Observaciones: '',
    prioridad: 'Media',
    email: '',
    telefono: '',
    geometry: null
  });
  
  const [errors, setErrors] = useState({});
  const [useMyLocation, setUseMyLocation] = useState(false);

  const getTiposIncidencia = () => {
    switch (formData.categoria) {
      case 'Limpieza Viaria':
        return TIPOS_LIMPIEZA;
      case 'Recogida RSU':
        return TIPOS_RECOGIDA;
      case 'Playas y Litoral':
        return TIPOS_PLAYAS;
      case 'Parques y Jardines':
        return TIPOS_JARDINES;
      default:
        return [];
    }
  };

  const handleGetLocation = () => {
    setUseMyLocation(true);
    if (navigator.geolocation) {
      toast.info('Obteniendo tu ubicación...');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            geometry: {
              x: position.coords.longitude,
              y: position.coords.latitude,
              spatialReference: { wkid: 4326 }
            }
          }));
          toast.success('Ubicación obtenida');
        },
        (error) => {
          toast.error('No se pudo obtener la ubicación');
          setUseMyLocation(false);
        }
      );
    } else {
      toast.error('Tu navegador no soporta geolocalización');
      setUseMyLocation(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.categoria) {
      newErrors.categoria = 'Selecciona una categoría';
    }
    
    if (!formData.tipo_incidencia && getTiposIncidencia().length > 0) {
      newErrors.tipo_incidencia = 'Selecciona un tipo de incidencia';
    }
    
    if (!formData.Direccion || formData.Direccion.length < 10) {
      newErrors.Direccion = 'La dirección debe tener al menos 10 caracteres';
    }
    
    if (!formData.Observaciones || formData.Observaciones.length < 10) {
      newErrors.Observaciones = 'Describe la incidencia (mínimo 10 caracteres)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, completa todos los campos requeridos');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Construir payload según categoría
      const payload = {
        procedencia: formData.procedencia,
        categoria: formData.categoria,
        Direccion: formData.Direccion,
        Observaciones: formData.Observaciones,
        prioridad: formData.prioridad,
        email: formData.email || null,
        telefono: formData.telefono || null,
        geometry: formData.geometry,
        fecha_de_la_incidencia: new Date().toISOString()
      };
      
      // Asignar el tipo según la categoría
      if (formData.categoria === 'Limpieza Viaria') {
        payload.tipo_incidencia = formData.tipo_incidencia;
      } else if (formData.categoria === 'Recogida RSU') {
        payload.tipo_incidenciaRec = formData.tipo_incidencia;
      } else if (formData.categoria === 'Playas y Litoral') {
        payload.tipo_incidenciaPlayas = formData.tipo_incidencia;
      } else if (formData.categoria === 'Parques y Jardines') {
        payload.tipo_incidenciaJardines = formData.tipo_incidencia;
      } else {
        payload.otros = formData.tipo_incidencia || 'Otro';
      }
      
      const response = await fetch(`${BACKEND_URL}/api/incidencias`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al crear incidencia');
      }
      
      const data = await response.json();
      setIncidenciaId(data.objectId);
      setIsSuccess(true);
      toast.success('¡Incidencia reportada correctamente!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al reportar la incidencia');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <Helmet>
          <title>Incidencia Reportada | Cuidemos Los Alcázares®</title>
        </Helmet>
        
        <section className="py-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[rgba(31,138,112,0.12)] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-[#1F8A70]" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#0A2540] mb-4">
              ¡Incidencia reportada!
            </h1>
            <p className="text-[#6F8AA8] text-lg mb-6">
              Hemos recibido tu reporte y nuestro equipo lo atenderá a la brevedad.
            </p>
            <div className="bg-[#F5F9FC] border border-[#CFE3F5] rounded-2xl p-6 mb-8">
              <p className="text-sm text-[#6F8AA8] mb-2">Número de incidencia</p>
              <p className="font-mono text-2xl text-[#0A2540] font-semibold">#{incidenciaId}</p>
            </div>
            {formData.email && (
              <p className="text-sm text-[#6F8AA8] mb-8">
                Te enviaremos actualizaciones al correo: <strong>{formData.email}</strong>
              </p>
            )}
            <button
              onClick={() => navigate('/ciudadania')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors"
            >
              <ArrowLeft size={18} />
              Volver a Ciudadanía
            </button>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Reportar Incidencia | Cuidemos Los Alcázares®</title>
        <meta name="description" content="Reporta incidencias de limpieza, recogida de residuos, playas y más en Los Alcázares" />
      </Helmet>
      
      {/* Hero */}
      <section className="pt-24 pb-12 px-6 bg-gradient-to-br from-[#FAFCFE] via-white to-[#F5F9FC]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(194,65,12,0.12)] flex items-center justify-center mx-auto mb-6">
            <AlertTriangle size={30} className="text-[#C2410C]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0A2540] mb-4">
            Reportar Incidencia
          </h1>
          <p className="text-[#6F8AA8] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Ayúdanos a mantener Los Alcázares limpio reportando incidencias.
            Tu colaboración es fundamental.
          </p>
        </div>
      </section>
      
      {/* Formulario */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Categoría */}
            <div>
              <label className="block text-sm font-semibold text-[#0A2540] mb-3">
                Categoría <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CATEGORIAS.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, categoria: cat.value, tipo_incidencia: '' }));
                      setErrors(prev => ({ ...prev, categoria: undefined }));
                    }}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.categoria === cat.value
                        ? 'border-[#1E5AA8] bg-[rgba(30,90,168,0.08)]'
                        : 'border-[#CFE3F5] hover:border-[#7DB9E8]'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">{cat.icon}</span>
                    <span className="text-sm font-medium text-[#0A2540]">{cat.label}</span>
                  </button>
                ))}
              </div>
              {errors.categoria && (
                <p className="mt-2 text-sm text-red-500">{errors.categoria}</p>
              )}
            </div>
            
            {/* Tipo de incidencia */}
            {formData.categoria && getTiposIncidencia().length > 0 && (
              <div>
                <label htmlFor="tipo_incidencia" className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Tipo de incidencia <span className="text-red-500">*</span>
                </label>
                <select
                  id="tipo_incidencia"
                  value={formData.tipo_incidencia}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, tipo_incidencia: e.target.value }));
                    setErrors(prev => ({ ...prev, tipo_incidencia: undefined }));
                  }}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.tipo_incidencia ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] transition-all`}
                  disabled={isSubmitting}
                >
                  <option value="">Selecciona un tipo</option>
                  {getTiposIncidencia().map((tipo) => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
                {errors.tipo_incidencia && (
                  <p className="mt-2 text-sm text-red-500">{errors.tipo_incidencia}</p>
                )}
              </div>
            )}
            
            {/* Dirección */}
            <div>
              <label htmlFor="direccion" className="block text-sm font-semibold text-[#0A2540] mb-2">
                Dirección o ubicación <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="direccion"
                  value={formData.Direccion}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, Direccion: e.target.value }));
                    setErrors(prev => ({ ...prev, Direccion: undefined }));
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl border ${errors.Direccion ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] transition-all`}
                  placeholder="Ej: Calle Mayor 123, cerca del parque"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={handleGetLocation}
                  className="px-4 py-3 rounded-xl border-2 border-[#CFE3F5] text-[#1E5AA8] hover:bg-[#FAFCFE] transition-colors flex items-center gap-2"
                  disabled={useMyLocation || isSubmitting}
                >
                  <MapPin size={18} />
                  {useMyLocation ? 'Ubicación obtenida' : 'Mi ubicación'}
                </button>
              </div>
              {errors.Direccion && (
                <p className="mt-2 text-sm text-red-500">{errors.Direccion}</p>
              )}
            </div>
            
            {/* Observaciones */}
            <div>
              <label htmlFor="observaciones" className="block text-sm font-semibold text-[#0A2540] mb-2">
                Descripción de la incidencia <span className="text-red-500">*</span>
              </label>
              <textarea
                id="observaciones"
                value={formData.Observaciones}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, Observaciones: e.target.value }));
                  setErrors(prev => ({ ...prev, Observaciones: undefined }));
                }}
                rows={5}
                className={`w-full px-4 py-3 rounded-xl border ${errors.Observaciones ? 'border-red-500' : 'border-[#CFE3F5]'} focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] transition-all resize-none`}
                placeholder="Describe la incidencia con el mayor detalle posible..."
                disabled={isSubmitting}
              />
              {errors.Observaciones && (
                <p className="mt-2 text-sm text-red-500">{errors.Observaciones}</p>
              )}
            </div>
            
            {/* Prioridad */}
            <div>
              <label className="block text-sm font-semibold text-[#0A2540] mb-3">Prioridad</label>
              <div className="flex gap-3">
                {PRIORIDADES.map((prio) => (
                  <button
                    key={prio.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, prioridad: prio.value }))}
                    className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                      formData.prioridad === prio.value
                        ? `${prio.color} border-current`
                        : 'border-[#CFE3F5] text-[#6F8AA8] hover:border-[#7DB9E8]'
                    }`}
                    disabled={isSubmitting}
                  >
                    {prio.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Datos de contacto (opcional) */}
            <div className="bg-[#F5F9FC] border border-[#CFE3F5] rounded-2xl p-6">
              <h3 className="font-semibold text-[#0A2540] mb-4">Datos de contacto (opcional)</h3>
              <p className="text-sm text-[#6F8AA8] mb-4">
                Si dejas tu email o teléfono, te informaremos cuando atendamos tu incidencia.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#0A2540] mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] transition-all"
                    placeholder="tu@email.com"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-[#0A2540] mb-2">Teléfono</label>
                  <input
                    type="tel"
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] transition-all"
                    placeholder="+34 612 345 678"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
            
            {/* Botones */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/ciudadania')}
                className="flex-1 px-8 py-4 rounded-full border-2 border-[#CFE3F5] text-[#0A2540] text-sm font-medium hover:bg-[#FAFCFE] transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9A3412] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar incidencia'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}